import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIStatusPanel from '@/shared/components/panels/WaveUIStatusPanel';
import WaveUINoDataOverlay from '@/shared/components/utilities/WaveUINoDataOverlay';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import CustomURLParams from '@/shared/constants/customURLParams';
import { AppPath } from '@/shared/constants/path';
import Order from '@/shared/types/order/order.type';

type OrdersPanelProps = {
  orders: Order[];
  customerId: string;
};

const OrdersPanel: FC<OrdersPanelProps> = ({ orders, customerId }) => {
  const { t } = useTranslation(['common', 'customers']);

  return (
    <WaveUIAccordion title={t('common:orders')} defaultExpanded>
      <Stack
        spacing={2}
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
          mx: -2,
          px: 2,
          py: 1,
        }}
      >
        {orders.length === 0 && (
          <WaveUINoDataOverlay size='small' title={t('customers:noOrders')} />
        )}
        {orders.map((order) => (
          <WaveUIStatusPanel
            key={order.in_order_id}
            id={order.in_order_id}
            statuses={[t(order.order_status, { ns: 'common:status' })]}
            total={order.order_total}
            created={order.ts_created}
            href={AppPath.Orders + `/${order.in_order_id}`}
          />
        ))}
      </Stack>

      <Stack
        direction='row'
        spacing={2}
        justifyContent={orders.length > 0 ? 'flex-end' : 'center'}
      >
        {orders.length > 0 && (
          <Button
            variant='outlined'
            color='primary'
            component={Link}
            to={`${AppPath.Orders}?${CustomURLParams.WithPredefinedFilters}=true`}
          >
            {t('common:viewAll')}
          </Button>
        )}
        <Button
          variant='contained'
          color='primary'
          component={Link}
          to={`${AppPath.Orders}/create?customerId=${customerId}`}
        >
          {t('customers:createOrder')}
        </Button>
      </Stack>
    </WaveUIAccordion>
  );
};

export default OrdersPanel;
